/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { createPortal } from 'react-dom';
import "../../styles/components/applicationTracker/jobDetailsModal.scss";
import JobDescriptionIcon from "../../assets/job-description.svg";
import ResumeIcon from "../../assets/document-icon.svg";
// Removed the import for useApplicationModal due to the error
import axiosClient from '../../axios-client';
import { blobStorageService } from '../../services/blobStorage.service';
import companyInfoService, { CompanyInfo } from '../../services/companyInfo.service';
import { useNavigate } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import DeleteConfirmModal from './DeleteConfirmModal';
import ApplicationResume from './ApplicationResume';
import ApplicationCoverLetter from './ApplicationCoverLetter';
import { addUtmParameters } from "../../utils/utm";

export interface JobData {
  _id: string;
  title: string;
  original_title?: string;
  company: string;
  location: string;
  location_hub?: string;
  summary?: string;
  qualifications?: string[];
  preferredQualifications?: string[];
  responsibilities?: string[];
  environment?: string;
  workEnvironment?: string;
  work_environment?: string;
  skills?: string | string[];
  salary?: string;
  experience_level?: string;
  jobType?: string;
  tailored_flag?: string;
  days_to_live?: any;
  job_url?: string;
  status?: 'applied' | 'interviewing' | 'offer';
  appliedDate?: string;
  lastStatusUpdate?: string;
  isDemo?: boolean;
  logo?: string;
  _preserveLogoState?: boolean;
}

interface JobDetailsModalProps {
  job: JobData;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange?: (newStatus: 'applied' | 'interviewing' | 'offer') => void;
  onDelete?: (jobId: string) => void;
}

// Add this interface for application documents
interface ApplicationDocuments {
  resume: any;
  coverLetter: string | null;
  dateApplied?: string | null;
}

// Add this for caching
const documentsCache = new Map<string, ApplicationDocuments>();

const JobDetailsModal: React.FC<JobDetailsModalProps> = ({
  job: initialJob,
  isOpen,
  onClose,
  onStatusChange,
  onDelete,
}) => {
  const [selectedSection, setSelectedSection] = useState("job-details");
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
  const [currentStatus, setCurrentStatus] = useState<'applied' | 'interviewing' | 'offer'>(initialJob.status || 'applied');
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<JobData>(initialJob);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoVariant, setLogoVariant] = useState<'horizontal' | 'square' | 'vertical' | null>(null);
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // New state for application documents with better typing
  const [applicationDocuments, setApplicationDocuments] = useState<ApplicationDocuments | null>(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setShowDeleteConfirm(false);
      setHasAnimated(false);
      setError(null);
      setApplicationDocuments(null);
      setDocumentsError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isOpen]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // Add effect to fetch full job details
  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!isOpen) return;
      
      setIsLoading(true);
      
      // For demo jobs, use the initial data without fetching
      if (initialJob.isDemo || initialJob.jobType === "DEMO" || initialJob._id.startsWith('demo-')) {
        setJob(initialJob);
        setIsLoading(false);
        setError(null);
        return;
      }

      // For real jobs, fetch from server
      try {
        const response = await axiosClient.get(`/jobs/${initialJob._id}`);
        setJob(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching job details:', err);
        setError('Failed to load job details');
        // Keep the initial job data if fetch fails
        setJob(initialJob);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobDetails();
  }, [isOpen, initialJob]);

  // Fetch application documents when modal opens
  useEffect(() => {
    const fetchApplicationDocuments = async () => {
      if (!isOpen || !job._id) return;

      // Skip fetching for demo jobs and set empty documents immediately
      if (job.isDemo || job.jobType === "DEMO" || job._id.startsWith('demo-')) {
        const emptyDocuments: ApplicationDocuments = {
          resume: null,
          coverLetter: null
        };
        setApplicationDocuments(emptyDocuments);
        setDocumentsLoading(false);
        setDocumentsError(null);
        return;
      }

      // Check cache first
      const cachedDocuments = documentsCache.get(job._id);
      if (cachedDocuments) {
        setApplicationDocuments(cachedDocuments);
        return;
      }

      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user._id) {
          setDocumentsError('User not found');
          return;
        }

        setDocumentsLoading(true);
        const response = await axiosClient.get(`/application-documents/${job._id}`, {
          params: {
            userId: user._id
          }
        });

        if (response.data) {
          const extractDate = (raw: any): string | null => {
            if (!raw) return null;
            // Handle MongoDB date object { $date: "..." }
            if (typeof raw === 'object' && raw.$date) return raw.$date;
            if (typeof raw === 'string') return raw;
            return null;
          };

          const documents: ApplicationDocuments = {
            resume: response.data.resume || null,
            coverLetter: response.data.coverLetter || null,
            dateApplied: extractDate(response.data.dateApplied) || extractDate(response.data.createdAt)
          };
          
          // Update cache
          documentsCache.set(job._id, documents);
          
          setApplicationDocuments(documents);
          setDocumentsError(null);
        }
      } catch (err) {
        console.error('Error fetching application documents:', err);
        setDocumentsError('Failed to load application documents');
        setApplicationDocuments(null);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchApplicationDocuments();
  }, [isOpen, job._id]);

  // Add effect to fetch company info
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (!isOpen || !initialJob.company) return;
      
      // Skip fetching for demo jobs since demo content is hardcoded
      if (initialJob.isDemo || initialJob.jobType === "DEMO" || initialJob._id.startsWith('demo-')) {
        setCompanyInfo(null); // Demo jobs use hardcoded company info in render
        return;
      }
      
      try {
        const companyData = await companyInfoService.getCompanyInfo(initialJob.company);
        if (companyData) {
          setCompanyInfo(companyData);
        }
      } catch (error) {
        // Company info fetch failure shouldn't affect job details display
      }
    };

    // Fetch in parallel with job details but don't block main flow
    fetchCompanyInfo();
  }, [isOpen, initialJob.company]);

  // Update the close handler
  const handleClose = () => {
    setShowDeleteConfirm(false);
    setHasAnimated(false);
    setError(null);
    onClose();
  };

  // Update logo loading logic
  useEffect(() => {
    const loadLogo = async () => {
      if (!job.company) return;

      // For demo jobs, use the preserved logo
      if ((job.isDemo || job.jobType === "DEMO" || job._id.startsWith('demo-')) && job._preserveLogoState && job.logo) {
        setLogoUrl(job.logo);
        setLogoVariant('square');
        return;
      }

      // For real jobs, fetch the logo
      try {
        const logo = await blobStorageService.getBestLogoVariant(job.company);
        if (logo) {
          setLogoUrl(logo.url);
          setLogoVariant(logo.variant);
        }
      } catch (err) {
        console.error('Error loading company logo:', err);
      }
    };

    loadLogo();
  }, [job]);

  // Add helper function to normalize job type strings
  const normalizeJobType = (jobType: string): string => {
    if (!jobType) return '';
    return jobType
      .replace(/^Job Type:\s*/i, '') // strip label if present
      .replace(/^['"]|['"]$/g, '')   // remove surrounding quotes
      .toLowerCase()
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Extract job type with fallback
  const extractJobType = (jobObj: JobData): string => {
    if (jobObj.jobType && jobObj.jobType.trim() !== '') {
      return jobObj.jobType;
    }
    // Fallback: assume full-time if unspecified
    return 'Full Time';
  };

  // For sidebar display – Title Case (e.g., "Full-Time")
  const normalizeJobTypeForDisplay = (jobTypeRaw: string): string => {
    if (!jobTypeRaw) return 'Not specified';
    const normalizedType = normalizeJobType(jobTypeRaw);

    switch (normalizedType) {
      case 'full time':
      case 'fulltime':
      case 'full-time':
        return 'Full-Time';
      case 'part time':
      case 'parttime':
      case 'part-time':
        return 'Part-Time';
      case 'internship':
      case 'intern':
        return 'Internship';
      case 'contract':
      case 'contractor':
      case 'contractual':
        return 'Contract';
      default:
        // Capitalize first letter of each word
        return normalizedType.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
  };

  // Update getJobTypeClass & formatJobType to use extract function
  const getJobTypeClass = (jobTypeRaw: string) => {
    const jobType = normalizeJobType(jobTypeRaw);
    if (!jobType) return "";
    
    if (jobType === "DEMO") return "demo";
    
    switch (jobType) {
      case "full time":
        return "full-time";
      case "part time":
        return "part-time";
      case "internship":
        return "internship";
      case "contract":
        return "contract";
      default:
        return "";
    }
  };

  const formatJobType = (jobTypeRaw: string) => {
    const jobType = normalizeJobType(jobTypeRaw);
    if (!jobType) return "";
    
    if (jobType === "DEMO") return "DEMO";
    
    switch (jobType) {
      case "full time":
        return "FULL-TIME";
      case "part time":
        return "PART-TIME";
      default:
        return jobTypeRaw.toUpperCase();
    }
  };

  const getPostedDate = (daysToLive: number | undefined) => {
    console.log("days_to_live value:", daysToLive);
    if (daysToLive === undefined || daysToLive === null) {
      return "Recently Posted";
    }

    const totalDays = 15;
    const daysAgo = totalDays - daysToLive;

    switch (daysAgo) {
      case 0:
        return "Today";
      case 1:
        return "Yesterday";
      default:
        return `${daysAgo} days ago`;
    }
  };

  const getAppliedDateText = () => {
    const raw = job.appliedDate || applicationDocuments?.dateApplied;
    if (!raw) return 'Unknown';
    const appliedDate = new Date(typeof raw === 'object' && (raw as any).$date ? (raw as any).$date : raw);
    const now = new Date();
    const diffTime = now.getTime() - appliedDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 3600 * 24));
    switch (diffDays) {
      case 0:
        return 'Today';
      case 1:
        return 'Yesterday';
      default:
        return `${diffDays} days ago`;
    }
  };

  const getStatusClass = (daysToLiveRaw: any): string => {
    const daysToLive = typeof daysToLiveRaw === 'object' && daysToLiveRaw?.$numberInt
      ? parseInt(daysToLiveRaw.$numberInt)
      : typeof daysToLiveRaw === 'number'
        ? daysToLiveRaw
        : 0;

    if (daysToLive >= 10) return 'recent'; // green
    if (daysToLive >= 5) return 'moderate'; // orange
    return 'old';                            // red
  };

  const handleButtonClick = () => {
    if (job.isDemo || job.jobType === "DEMO") {
      navigate('/job-search');
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = (success: boolean) => {
    if (success && onDelete) {
      // Clear cache for this job
      documentsCache.delete(job._id);
      onDelete(job._id);
    }
    setShowDeleteConfirm(false);
    onClose();
  };

  if (!isOpen) return null;

  const tabsConfig = [
    {
      id: "job-details",
      label: "Job Details",
      icon: (
        <img
          src={JobDescriptionIcon}
          alt="Job Description Icon"
          width="16"
          height="16"
          className="tab-icon"
          style={{ color: 'currentColor' }}
        />
      ),
    },
    {
      id: "resume",
      label: "Resume",
      icon: (
        <img
          src={ResumeIcon}
          alt="Document Icon"
          width="16"
          height="16"
          className="tab-icon"
          style={{ color: 'currentColor' }}
        />
      ),
    },
    {
      id: "cover-letter",
      label: "Cover Letter",
      icon: (
        <img
          src={ResumeIcon}
          alt="Document Icon"
          width="16"
          height="16"
          className="tab-icon"
          style={{ color: 'currentColor' }}
        />
      ),
    }
  ];

  const renderContent = () => {
    switch (selectedSection) {
      case "resume":
        if (documentsLoading) {
          return (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading resume...</p>
            </div>
          );
        }
        if (documentsError) {
          return (
            <div className="error-container">
              <p>{documentsError}</p>
            </div>
          );
        }
        return <ApplicationResume 
          jobId={job._id} 
          resumeData={applicationDocuments?.resume || null} 
        />;
      case "cover-letter":
        if (documentsLoading) {
          return (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading cover letter...</p>
            </div>
          );
        }
        if (documentsError) {
          return (
            <div className="error-container">
              <p>{documentsError}</p>
            </div>
          );
        }
        return <ApplicationCoverLetter 
          jobId={job._id} 
          coverLetterContent={applicationDocuments?.coverLetter || null} 
        />;
      case "job-details":
      default:
        const jobToDisplay = job;
        const isDemo = jobToDisplay.isDemo || jobToDisplay.jobType === "DEMO";

        return (
          <>
            {isDemo && (
              <div className="demo-notification">
                <div className="demo-notification-content">
                  <div className="demo-text">
                    <strong>Demo Job Tile</strong>
                    <p>This is a preview to help you understand how the application tracker works. Once you start applying to jobs, your actual applications will appear here instead.</p>
                  </div>
                </div>
              </div>
            )}
            <div className="content-container">
              <div className="left-column">
                {/* About the Job */}
                <div className="detail-section">
                  <h3 className="section-heading">About the Job:</h3>
                  <div className="section-content">
                    {isDemo ? (
                      <p>
                        QuickCruit is seeking a talented {jobToDisplay.title} to join our growing team. This role offers an opportunity to work on innovative projects while developing your professional skills. The ideal candidate is passionate about creating impactful solutions and thrives in a collaborative environment.
                      </p>
                    ) : (
                      <p>{jobToDisplay.summary || "No description provided."}</p>
                    )}
                  </div>
                </div>

                {/* Requirements */}
                <div className="detail-section">
                  <h3 className="section-heading">Requirements:</h3>
                  <div className="section-content">
                    {isDemo ? (
                      <ul className="requirements-list">
                        <li>
                          <span className="bullet-point">•</span>
                          <span className="requirement-text">Bachelor's degree in Marketing, Business, or related field</span>
                        </li>
                        <li>
                          <span className="bullet-point">•</span>
                          <span className="requirement-text">3+ years of experience in product marketing or similar role</span>
                        </li>
                        <li>
                          <span className="bullet-point">•</span>
                          <span className="requirement-text">Strong analytical and strategic thinking abilities</span>
                        </li>
                        <li>
                          <span className="bullet-point">•</span>
                          <span className="requirement-text">Excellent written and verbal communication skills</span>
                        </li>
                        <li>
                          <span className="bullet-point">•</span>
                          <span className="requirement-text">Experience with market research and competitive analysis</span>
                        </li>
                      </ul>
                    ) : jobToDisplay.qualifications && jobToDisplay.qualifications.length > 0 ? (
                      <ul className="requirements-list">
                        {jobToDisplay.qualifications.map((req, index) => (
                          <li key={index}>
                            <span className="bullet-point">•</span>
                            <span className="requirement-text">{req}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No requirements specified.</p>
                    )}
                  </div>
                </div>

                {/* Responsibilities */}
                <div className="detail-section">
                  <h3 className="section-heading">Responsibilities:</h3>
                  <div className="section-content">
                    {isDemo ? (
                      <ul className="responsibilities-list">
                        <li>
                          <span className="bullet-point">•</span>
                          <span className="responsibility-text">Develop and execute product marketing strategies</span>
                        </li>
                        <li>
                          <span className="bullet-point">•</span>
                          <span className="responsibility-text">Create compelling product messaging and positioning</span>
                        </li>
                        <li>
                          <span className="bullet-point">•</span>
                          <span className="responsibility-text">Conduct market research and analyze competitive landscape</span>
                        </li>
                        <li>
                          <span className="bullet-point">•</span>
                          <span className="responsibility-text">Collaborate with cross-functional teams to drive product success</span>
                        </li>
                        <li>
                          <span className="bullet-point">•</span>
                          <span className="responsibility-text">Track and report on key performance metrics</span>
                        </li>
                      </ul>
                    ) : jobToDisplay.responsibilities && jobToDisplay.responsibilities.length > 0 ? (
                      <ul className="responsibilities-list">
                        {jobToDisplay.responsibilities.map((resp, index) => (
                          <li key={index}>
                            <span className="bullet-point">•</span>
                            <span className="responsibility-text">{resp}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No responsibilities specified.</p>
                    )}
                  </div>
                </div>

                {/* Skills */}
                <div className="detail-section">
                  <h3 className="section-heading">Skills:</h3>
                  <div className="section-content">
                    {isDemo ? (
                      <div className="skills">
                        <span className="skill-badge">Product Marketing</span>
                        <span className="skill-badge">Market Research</span>
                        <span className="skill-badge">Strategic Planning</span>
                        <span className="skill-badge">Data Analysis</span>
                        <span className="skill-badge">Project Management</span>
                        <span className="skill-badge">Communication</span>
                      </div>
                    ) : (
                      <div className="skills">
                        {jobToDisplay.skills ? (
                          Array.isArray(jobToDisplay.skills) ? (
                            jobToDisplay.skills.map((skill, index) => (
                              <span key={index} className="skill-badge">
                                {skill.replace(/^•\s*/, "").trim()}
                              </span>
                            ))
                          ) : (
                            jobToDisplay.skills.split(",").map((skill, index) => (
                              <span key={index} className="skill-badge">
                                {skill.trim()}
                              </span>
                            ))
                          )
                        ) : (
                          <p>No skills specified.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="right-column">
                <div className="job-info-box">
                  {isDemo ? (
                    <>
                      <h4 className="section-heading">
                        Applied
                      </h4>
                      <div className="section-content"><p>Today</p></div>

                      <h4 className="section-heading">Salary Range</h4>
                      <div className="section-content"><p>$180,000—$250,000 USD</p></div>

                      <h4 className="section-heading">Experience Level</h4>
                      <div className="section-content"><p>Mid Career (3+ years)</p></div>

                      <h4 className="section-heading">Job Type</h4>
                      <div className="section-content"><p>Full-Time</p></div>

                      <h4 className="section-heading">Work Environment</h4>
                      <div className="section-content"><p>Hybrid</p></div>

                      <h4 className="section-heading about-company">About the Company</h4>
                      <div className="section-content">
                        <div className="company-description-container">
                          <p className="about-company-text">
                            QuickCruit is a leading technology company revolutionizing the job search and application process. We're dedicated to helping job seekers find and land their dream roles through innovative solutions and cutting-edge technology.
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <h4 className="section-heading">
                        Applied
                      </h4>
                      <div className="section-content"><p>{getAppliedDateText()}</p></div>

                      <h4 className="section-heading">Salary Range</h4>
                      <div className="section-content"><p>{jobToDisplay.salary || "N/A"}</p></div>

                      <h4 className="section-heading">Experience Level</h4>
                      <div className="section-content"><p>{jobToDisplay.experience_level || "Not specified"}</p></div>

                      <h4 className="section-heading">Job Type</h4>
                      <div className="section-content"><p>{normalizeJobTypeForDisplay(extractJobType(jobToDisplay))}</p></div>

                      <h4 className="section-heading">Work Environment</h4>
                      <div className="section-content"><p>{jobToDisplay.workEnvironment || jobToDisplay.environment || jobToDisplay.work_environment || "Not specified"}</p></div>

                      <h4 className="section-heading about-company">About the Company</h4>
                      <div className="section-content" data-replacing-skeleton="true">
                        <div className="company-description-container">
                          <p className="about-company-text">
                            {companyInfo && companyInfo.description 
                              ? companyInfo.description 
                              : `${jobToDisplay.company} is a leader in its industry providing innovative solutions and services.`}
                          </p>
                          {companyInfo && companyInfo.website_url && (
                            <a 
                              href={addUtmParameters(companyInfo.website_url)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="external-link"
                            >
                              Learn More
                              <svg className="external-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 3V9H19V6.414L11.207 14.207L9.793 12.793L17.586 5H15V3H21Z" fill="currentColor"/>
                                <path d="M19 19H5V5H12V3H5C3.897 3 3 3.897 3 5V19C3 20.103 3.897 21 5 21H19C20.103 21 21 20.103 21 19V12H19V19Z" fill="currentColor"/>
                              </svg>
                            </a>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        );
    }
  };

  const handleTabChange = (tabId: string) => {
    setSelectedSection(tabId);
  };

  return (
    <>
      {createPortal(
        <div className={`modal-backdrop ${isOpen ? "show" : ""}`}>
          <div className={`modal-content ${isOpen ? "show" : ""}`}>
            <div className="sticky-container">
              <div className="modal-header">
                <div className="tabs">
                  {tabsConfig.map((tab) => (
                    <div
                      key={tab.id}
                      className={`tab ${selectedSection === tab.id ? "selected" : ""}`}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      <span className="tab-icon">{tab.icon}</span>
                      <span>{tab.label}</span>
                    </div>
                  ))}
                </div>
                <button className="close-button" onClick={handleClose}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.2253 4.81108C5.83477 4.42056 5.20161 4.42056 4.81108 4.81108C4.42056 5.20161 4.42056 5.83477 4.81108 6.2253L10.5858 12L4.81114 17.7747C4.42062 18.1652 4.42062 18.7984 4.81114 19.1889C5.20167 19.5794 5.83483 19.5794 6.22535 19.1889L12 13.4142L17.7747 19.1889C18.1652 19.5794 18.7984 19.5794 19.1889 19.1889C19.5794 18.7984 19.5794 18.1652 19.1889 17.7747L13.4142 12L19.189 6.2253C19.5795 5.83477 19.5795 5.20161 19.189 4.81108C18.7985 4.42056 18.1653 4.42056 17.7748 4.81108L12 10.5858L6.2253 4.81108Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
              {selectedSection === "job-details" && (
                <>
                  <div className={`header-section ${hasAnimated ? 'animate' : ''} ${selectedSection === "job-details" ? 'visible' : ''}`}>
                    <div className="job-title-section">
                      <div className="top-row">
                        <div className={`company-logo-wrapper ${logoVariant === 'horizontal' ? 'horizontal-logo-container' : ''}`}>
                          {logoUrl ? (
                            <img
                              src={logoUrl}
                              alt={`${job.company} logo`}
                              className={`company-logo ${logoVariant === 'horizontal' ? 'horizontal-logo' : ''}`}
                            />
                          ) : (
                            <div className="logo-placeholder" />
                          )}
                        </div>
                        {extractJobType(job) && (
                          <span
                            className={`job-type-tag ${getJobTypeClass(extractJobType(job))}`}
                          >
                            {formatJobType(extractJobType(job))}
                          </span>
                        )}
                      </div>
                      <div className="bottom-row">
                        <div className="job-title">
                          <h2>{job.title}</h2>
                          <p className="company-location">
                            {job.company} • {job.location}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="button-group">
                      {!job.isDemo && !job.jobType?.includes("DEMO") && (
                        <button className="delete-job-button" onClick={handleDeleteClick}>
                          <div className="trash-icon">
                            <FaTrash size={16} />
                          </div>
                          Delete
                        </button>
                      )}
                      <button
                        className="tailor-button"
                        onClick={handleButtonClick}
                      >
                        {job.isDemo || job.jobType === "DEMO" ? "Land Your Dream Job 🚀" : "Mock Interview"}
                      </button>
                    </div>
                  </div>
                  <hr className="separator-line" />
                </>
              )}
            </div>
            <div className="modal-body">
              {isLoading ? (
                <div className="loading-overlay">
                  <div className="loading-spinner"></div>
                  <p>Loading job details...</p>
                </div>
              ) : error ? (
                <div className="error-message">
                  <p>{error}</p>
                  <button onClick={() => setError(null)}>Dismiss</button>
                </div>
              ) : (
                renderContent()
              )}
            </div>
          </div>
        </div>,
        document.getElementById('job-details-modal-root')!
      )}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          showModal={showDeleteConfirm}
          jobId={job._id}
          onConfirm={handleDeleteConfirm}
          setModalData={({ showModal }) => setShowDeleteConfirm(showModal)}
        />
      )}
    </>
  );
};

export default JobDetailsModal; 